import { CSRF_HEADER, type FolderId, type SavedFile } from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { type ReactNode, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { NativeSelect } from "@/components/NativeSelect";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "../api.ts";
import { ApiError, ok } from "../api-errors.ts";
import { canChange } from "../projects/capabilities.ts";
import { FolderField } from "../projects/FolderPicker.tsx";
import { type KnowledgeSettings, projectKeys, useProjects } from "../projects/queries.ts";
import { shortId } from "../projects/readers.ts";
import { useSession } from "../session.tsx";
import { AudienceLine } from "../trust/AudienceLine.tsx";
import { peopleAudience } from "../trust/audience.ts";
import { SuggestedTitleField, useSuggestedTitle } from "./SuggestedTitle.tsx";

export function SaveDialog(props: {
  open: boolean;
  onClose: () => void;
  /** Null from a standalone conversation: the user picks the project to save into. */
  projectId: string | null;
  conversationId: string;
  messageId: string;
  settings: KnowledgeSettings | undefined;
  /** The answer used web results (its trust is then external too). */
  webResults?: boolean;
  /** Code ran for the answer (agent-turn D20). */
  ranCode?: boolean;
  /** A title typed in "Save to wiki" before choosing "Save as a file instead". */
  initialTitle?: string;
}) {
  const { t } = useTranslation();
  // Kept here: the form is remounted for each picked target, the typed title is not lost.
  const typed = useState<string | null>(props.initialTitle?.trim() ? props.initialTitle : null);
  return (
    <AppDialog open={props.open} onClose={props.onClose} title={t("save.title")}>
      {props.projectId ? (
        <SaveForm {...props} projectId={props.projectId} target={null} typed={typed} />
      ) : (
        <TargetPicker capability="file.save_answer">
          {(target) => (
            <SaveForm
              key={target}
              {...props}
              projectId={target}
              target={target}
              settings={undefined}
              typed={typed}
            />
          )}
        </TargetPicker>
      )}
    </AppDialog>
  );
}

/**
 * The project an answer of a standalone conversation goes into: only projects where the user
 * may save (`capability`) and that are not frozen. The server checks again, and refuses a
 * project with a narrower residency in plain words.
 */
export function TargetPicker(props: {
  capability: "file.save_answer" | "file.upload" | "wiki.draft";
  children: (projectId: string) => ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  const projects = useProjects();
  const targets = (projects.data ?? []).filter((p) => canChange(p, props.capability));
  const [picked, setPicked] = useState<string | null>(null);
  const target = targets.find((p) => p.id === picked)?.id ?? targets[0]?.id ?? null;
  if (projects.isPending) {
    return <Skeleton className="h-9 w-full" />;
  }
  if (projects.isError) {
    return <Notice tone="danger">{projects.error.message}</Notice>;
  }
  if (target === null) {
    return <Notice>{t("save.noTarget")}</Notice>;
  }
  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${id}-target`}>{t("save.target")}</Label>
        <NativeSelect
          id={`${id}-target`}
          value={target}
          onChange={(e) => setPicked(e.currentTarget.value)}
        >
          {targets.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </NativeSelect>
        <p className="text-xs text-fg-muted">{t("save.targetHint")}</p>
      </div>
      {props.children(target)}
    </div>
  );
}

/**
 * Saves an answer as a project file. Before confirming, it shows who could read the file right
 * now: the answer's sources narrow its readers, so this may be fewer people than the project.
 */
function SaveForm(props: {
  onClose: () => void;
  /** Where the file goes: the conversation's project, or the picked target. */
  projectId: string;
  /** The picked target of a standalone conversation, sent with the save; else null. */
  target: string | null;
  conversationId: string;
  messageId: string;
  settings: KnowledgeSettings | undefined;
  webResults?: boolean;
  ranCode?: boolean;
  typed: [string | null, (value: string | null) => void];
}) {
  const { onClose, projectId, conversationId, messageId, settings, target } = props;
  const { t } = useTranslation();
  const { csrfToken, userId } = useSession();
  const queryClient = useQueryClient();
  const param = { conversationId, messageId };
  const preview = useQuery({
    queryKey: ["conversation-save", conversationId, messageId, projectId],
    // Who can read changes with access: always ask again when the dialog opens.
    gcTime: 0,
    retry: false,
    queryFn: async () =>
      ok(
        await api.api.conversations[":conversationId"].messages[":messageId"].save.$get({
          param,
          query: target ? { targetProjectId: target } : {},
        }),
      ),
  });
  const title = useSuggestedTitle({
    conversationId,
    messageId,
    sink: "file",
    target,
    typed: props.typed,
  });
  const [saved, setSaved] = useState<SavedFile | null>(null);
  // The Files root unless the user picks a folder (knowledge-folders).
  const [folderId, setFolderId] = useState<FolderId | null>(null);
  const projects = useProjects();
  const canOrganise = projects.data?.some(
    (p) => p.id === projectId && canChange(p, "folder.manage"),
  );
  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.api.conversations[":conversationId"].messages[":messageId"].save.$post(
          {
            param,
            json: {
              ...(title.value.trim() ? { title: title.value.trim() } : {}),
              ...(target ? { targetProjectId: target } : {}),
              ...(folderId ? { folderId } : {}),
            },
          },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSuccess: (file) => {
      setSaved(file);
      void queryClient.invalidateQueries({ queryKey: projectKeys.documents(projectId) });
    },
  });
  // Said before confirming (the origin line says external too, for any external source).
  const web = props.webResults === true;
  const level = (classification: string) =>
    settings?.scheme.find((l) => l.id === classification)?.label ?? classification;

  if (saved) {
    return (
      <div className="flex flex-col gap-4">
        <Notice tone="success" live>
          {t("save.saved", { title: saved.title })}
        </Notice>
        <DialogFooter>
          <Button onClick={onClose}>{t("save.done")}</Button>
        </DialogFooter>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (preview.data && !save.isPending) {
          save.mutate();
        }
      }}
    >
      {preview.isPending ? (
        <Skeleton className="h-24 w-full" />
      ) : preview.isError ? (
        <Notice tone="danger">{saveRefusal(preview.error, t)}</Notice>
      ) : (
        <>
          <section aria-label={t("audience.label")} className="flex flex-col gap-2">
            <AudienceLine
              restricted
              className="text-sm font-medium text-fg"
              text={peopleAudience(
                preview.data.readers.length,
                preview.data.readers.length === 1 && preview.data.readers[0]?.userId === userId,
              )}
              webResults={web}
              ranCode={props.ranCode === true}
            />
            <p className="text-sm text-fg-secondary">{t("save.readersExplain")}</p>
            {web ? <p className="text-sm text-fg-secondary">{t("save.webExplain")}</p> : null}
            <ul className="max-h-44 divide-y divide-line overflow-y-auto rounded-lg border border-line bg-surface-raised text-sm">
              {preview.data.readers.map((r) => (
                <li key={r.userId} className="px-3 py-2 text-fg">
                  {r.displayName ?? shortId(r.userId)}
                  {r.userId === userId ? (
                    <span className="text-fg-muted"> ({t("members.you")})</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
            <dt className="text-fg-muted">{t("save.classification")}</dt>
            <dd className="text-fg">{level(preview.data.classification)}</dd>
            <dt className="text-fg-muted">{t("save.origin")}</dt>
            <dd className="text-fg">{t(`files.originLabel.${preview.data.origin}`)}</dd>
          </dl>
          <SuggestedTitleField
            label={t("save.fileTitle")}
            placeholder={t("save.fileTitlePlaceholder")}
            title={title}
          />
          <FolderField
            projectId={projectId}
            section="files"
            value={folderId}
            onChange={setFolderId}
            canCreate={canOrganise}
          />
        </>
      )}
      {save.isError ? <Notice tone="danger">{saveRefusal(save.error, t)}</Notice> : null}
      <DialogFooter sticky>
        <Button type="button" variant="outline" onClick={onClose}>
          {t("save.cancel")}
        </Button>
        <Button type="submit" disabled={!preview.data || save.isPending}>
          {save.isPending ? t("save.saving") : t("save.submit")}
        </Button>
      </DialogFooter>
    </form>
  );
}

/** A refusal in plain words: the residency rule is the one a user can act on (pick another). */
export function saveRefusal(error: Error, t: TFunction): string {
  return error instanceof ApiError && error.code === "residency_narrower"
    ? t("save.residencyNarrower")
    : error.message;
}
